import React, { ComponentPropsWithoutRef, ElementType, useMemo } from 'react';
import createDOMPurify from 'dompurify';

/**
 * Private DOMPurify instance. Hooks and config are registered on this instance
 * only, so they cannot affect other callers and cannot accumulate across hot
 * module reloads the way hooks on the shared default instance would.
 *
 * This module is browser-only by design: `window` is read at import time, so it
 * cannot be imported from Node without a DOM. That is deliberate for this SPA.
 * Introducing SSR, prerendering or a non-jsdom test environment would mean
 * providing a DOM here (for example through `jsdom` or `isomorphic-dompurify`)
 * rather than changing the callers.
 */
const purify = createDOMPurify(window);

/**
 * Tags the CKEditor content never uses but DOMPurify's `html` profile would
 * otherwise allow. Form controls are the important ones: a `<form>` with a
 * password `<input>` inside a report description is a phishing surface even
 * though it carries no script. Verified against stored content: no row uses
 * any of these.
 */
const FORBIDDEN_TAGS = [
  'form',
  'input',
  'button',
  'select',
  'option',
  'optgroup',
  'textarea',
  'label',
  'fieldset',
  'legend',
  'datalist',
  'output',
  'progress',
  'meter',
  'audio',
  'video',
  'source',
  'track',
  'marquee',
  'style',
  'iframe',
  'object',
  'embed',
  'base',
  'link',
  'map',
  'area',
];

/**
 * Attributes removed regardless of tag.
 *
 * `class` matters more than it looks. The inline-style allowlist below is
 * pointless on its own while a class can pull in a rule the application
 * already ships: `<div class="ant-modal-mask">` would otherwise reproduce the
 * full-screen overlay that antd injects at runtime (`position: fixed;
 * inset: 0; z-index: 1000`) and cover the UI with attacker-supplied content.
 *
 * Dropping the attribute outright rather than allowlisting values is safe here:
 * across the content this app renders, the only classes present are Word paste
 * artifacts (`x0`, `MsoNoSpacing`, `candidates`), and none of them matches a
 * style rule in this codebase.
 *
 * Two cases would change that answer. Enabling CKEditor's class-driven features
 * (image alignment, table styles) means allowlisting exactly those class names
 * instead. And the course portal's content does rely on classes: its
 * `img.zoom` lightbox in backend/course/templates/_zoom-image.html keys off
 * one. That content is rendered by Django and never reaches this component, so
 * it is unaffected today, but moving the course pages into this app would mean
 * allowlisting those classes here first.
 */
const FORBIDDEN_ATTRIBUTES = ['class'];

/**
 * CSS properties inline `style` attributes may keep, as longhand names.
 *
 * DOMPurify does not filter CSS, so without this an attacker-supplied
 * `position: fixed; inset: 0; z-index: 99999` could cover the application UI
 * with a fake dialog, and `url(...)` could call out to an external host. The
 * list covers every property the stored content actually uses, plus the rest
 * of each property's family so shorthand declarations survive intact.
 *
 * Deliberately absent: position, inset/top/right/bottom/left, z-index,
 * transform, opacity, visibility, pointer-events, cursor, content, overflow,
 * background-image, animation, transition, filter and clip-path.
 */
const ALLOWED_CSS_PROPERTIES = [
  // text and font
  'color',
  'background-color',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'font-variant',
  'line-height',
  'letter-spacing',
  'word-spacing',
  'text-align',
  'text-decoration',
  'text-indent',
  'text-transform',
  'white-space',
  'vertical-align',
  'direction',
  // box
  'width',
  'height',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  // borders
  'border-top-width',
  'border-right-width',
  'border-bottom-width',
  'border-left-width',
  'border-top-style',
  'border-right-style',
  'border-bottom-style',
  'border-left-style',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'border-collapse',
  'border-spacing',
  // flow
  'display',
  'float',
];

/** Constructs that must never appear in a CSS value, whatever the property. */
const FORBIDDEN_CSS_VALUE = /url\s*\(|expression\s*\(|@import|\/\*/i;

/**
 * Rebuilds an element's `style` attribute from the allowed properties only.
 *
 * Values are read back through the CSSOM rather than parsed out of the
 * attribute text, so the browser has already resolved shorthands, casing and
 * CSS escapes (`pos\69 tion`) into normalized longhand names before the
 * allowlist is applied.
 */
const filterInlineStyle = (node: HTMLElement) => {
  const declarations: string[] = [];

  ALLOWED_CSS_PROPERTIES.forEach(property => {
    const value = node.style.getPropertyValue(property);
    if (value && !FORBIDDEN_CSS_VALUE.test(value)) {
      declarations.push(`${property}: ${value}`);
    }
  });

  if (declarations.length) {
    node.setAttribute('style', declarations.join('; '));
  } else {
    node.removeAttribute('style');
  }
};

/**
 * Links that open a new browsing context get `noopener noreferrer`, merged into
 * whatever `rel` the content already carries so tokens such as `nofollow` are
 * not lost. Any target value is accepted apart from `_self`, because the stored
 * content uses both `_blank` and `_new`.
 */
const hardenLink = (node: HTMLElement) => {
  const target = node.getAttribute('target')?.toLowerCase();
  if (!target || target === '_self') {
    return;
  }

  // Only "open in a new tab" is a shape this content needs. `_new` is a named
  // target that behaves like `_blank` anyway once `noopener` is set, so it is
  // normalized rather than preserved, and anything else is dropped.
  if (target !== '_blank' && target !== '_new') {
    node.removeAttribute('target');
    return;
  }
  node.setAttribute('target', '_blank');

  const rel = new Set(
    (node.getAttribute('rel') ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .map(token => token.toLowerCase())
  );
  rel.delete('opener');
  rel.add('noopener');
  rel.add('noreferrer');

  node.setAttribute('rel', [...rel].join(' '));
};

purify.addHook('afterSanitizeAttributes', currentNode => {
  const node = currentNode as HTMLElement;

  if (node.hasAttribute?.('style')) {
    filterInlineStyle(node);
  }

  if (node.tagName === 'A') {
    hardenLink(node);
  }
});

/**
 * Sanitizes a CKEditor/API-supplied HTML string so it can be rendered as markup.
 *
 * Keeps the formatting the stored content relies on (p, br, b/strong, em/i, u,
 * a[href], ul/ol/li, h1-h4, hr, img, tables, span, font, blockquote, pre, plus
 * lang, dir, align, data-* and allowlisted inline styles) while dropping
 * script, event handlers, javascript: URLs, SVG, MathML, iframes, form
 * controls, `class`, and layout CSS that could cover the UI.
 *
 * Two things this deliberately does not try to do. It does not police CSS
 * values, so an extreme `font-size` or negative margin can still disturb
 * layout; and it does not stop an allowed `<img src>` from reaching an external
 * host. Both belong to the Content-Security-Policy work, not here.
 */
export const sanitizeHtml = (html?: string | null): string =>
  purify.sanitize(html ?? '', {
    USE_PROFILES: { html: true },
    FORBID_TAGS: FORBIDDEN_TAGS,
    FORBID_ATTR: FORBIDDEN_ATTRIBUTES,
    // Prefixes any `id`/`name` the content carries so it cannot collide with
    // application DOM properties. Safe here because no stored content uses
    // fragment links that would depend on a particular id.
    SANITIZE_NAMED_PROPS: true,
    // `target` is not in DOMPurify's defaults, and CKEditor's "open in new tab"
    // option needs it. The predicate form keeps it scoped to anchors.
    ADD_ATTR: (attributeName: string, tagName: string) =>
      attributeName === 'target' && tagName === 'a',
  });

type SafeHtmlOwnProps<T extends ElementType> = {
  /** Element or styled-component to render. Defaults to a div. */
  as?: T;
  /** Untrusted HTML string. Sanitized before it reaches the DOM. */
  html?: string | null;
};

export type SafeHtmlProps<T extends ElementType> = SafeHtmlOwnProps<T> &
  Omit<
    ComponentPropsWithoutRef<T>,
    keyof SafeHtmlOwnProps<T> | 'children' | 'dangerouslySetInnerHTML'
  >;

/**
 * The only place in the app allowed to set inner HTML. Every sink goes through
 * here so that a raw `dangerouslySetInnerHTML` anywhere else is a lint error
 * (see the `react/no-danger` rule in .eslintrc.json).
 *
 * The element passed through `as` is expected to be an ordinary HTML container.
 * The sanitized output is an HTML fragment, so rendering it into a foreign
 * namespace such as `svg` is not a supported use.
 */
const SafeHtml = <T extends ElementType = 'div'>({ as, html, ...rest }: SafeHtmlProps<T>) => {
  const Component = (as ?? 'div') as ElementType;
  const __html = useMemo(() => sanitizeHtml(html), [html]);

  // eslint-disable-next-line react/no-danger
  return <Component {...rest} dangerouslySetInnerHTML={{ __html }} />;
};

export default SafeHtml;
